import React, { useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, EyeIcon } from 'lucide-react';
// Sample order data
const orders = [{
  id: 'ORD-7245',
  customer: 'John Smith',
  email: 'john.smith@example.com',
  date: 'Aug 06, 2023',
  status: 'Completed',
  total: 1249.0,
  items: 3
}, {
  id: 'ORD-7244',
  customer: 'Emily Johnson',
  email: 'emily.j@example.com',
  date: 'Aug 05, 2023',
  status: 'Processing',
  total: 2084.0,
  items: 1
}, {
  id: 'ORD-7243',
  customer: 'Michael Brown',
  email: 'michael.b@example.com',
  date: 'Aug 05, 2023',
  status: 'Shipped',
  total: 349.0,
  items: 2
}, {
  id: 'ORD-7242',
  customer: 'Sarah Wilson',
  email: 'sarah.w@example.com',
  date: 'Aug 04, 2023',
  status: 'Cancelled',
  total: 899.0,
  items: 4
}, {
  id: 'ORD-7241',
  customer: 'David Lee',
  email: 'david.lee@example.com',
  date: 'Aug 03, 2023',
  status: 'Completed',
  total: 1599.0,
  items: 2
}];
export const OrdersOverview: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'Processing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'Shipped':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'Cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  const filteredOrders = selectedStatus === 'all' ? orders : orders.filter(order => order.status.toLowerCase() === selectedStatus.toLowerCase());
  return <div className="container mx-auto px-4 py-8" data-id="element-768">
      <div className="flex justify-between items-center mb-6" data-id="element-769">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-770">
          Orders Overview
        </h1>
        <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md flex items-center" data-id="element-771">
          <DownloadIcon size={18} className="mr-1" data-id="element-772" />
          Export Orders
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden" data-id="element-773">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-774">
          <div className="relative flex-grow max-w-md" data-id="element-775">
            <input type="text" placeholder="Search orders..." className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-776" />
            <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-777" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-778">
            <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center" data-id="element-779">
              <FilterIcon size={18} className="mr-1" data-id="element-780" />
              Filter
            </button>
            <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-781">
              <option value="all" data-id="element-782">All Statuses</option>
              <option value="completed" data-id="element-783">Completed</option>
              <option value="processing" data-id="element-784">Processing</option>
              <option value="shipped" data-id="element-785">Shipped</option>
              <option value="cancelled" data-id="element-786">Cancelled</option>
            </select>
            <select className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500" data-id="element-787">
              <option value="newest" data-id="element-788">Newest First</option>
              <option value="oldest" data-id="element-789">Oldest First</option>
              <option value="highest" data-id="element-790">Highest Amount</option>
              <option value="lowest" data-id="element-791">Lowest Amount</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-792">
          <table className="w-full" data-id="element-793">
            <thead className="bg-gray-50 dark:bg-gray-900" data-id="element-794">
              <tr data-id="element-795">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-796">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-797">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-798">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-799">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-800">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-801">
                  Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-802">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-803">
              {filteredOrders.map(order => <tr key={order.id} data-id="element-804">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-805">
                    #{order.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-806">
                    <div data-id="element-807">
                      <div className="text-sm text-gray-900 dark:text-white" data-id="element-808">
                        {order.customer}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-809">
                        {order.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-810">
                    {order.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-811">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(order.status)}`} data-id="element-812">
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-813">
                    {order.items}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-814">
                    ${order.total.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm" data-id="element-815">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center justify-end" data-id="element-816">
                      <EyeIcon size={16} className="mr-1" data-id="element-817" />
                      View
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-818">
          <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-819">
            Showing <span className="font-medium" data-id="element-820">1</span> to{' '}
            <span className="font-medium" data-id="element-821">{filteredOrders.length}</span> of{' '}
            <span className="font-medium" data-id="element-822">{filteredOrders.length}</span> results
          </div>
          <div className="flex space-x-2" data-id="element-823">
            <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50" data-id="element-824">
              Previous
            </button>
            <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50" data-id="element-825">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
};